import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, TrendingUp, AlertTriangle, Receipt } from "lucide-react";
import { expenseCategoryLabel } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/cn";
import { BarChart } from "@/components/charts/BarChart";
import { Donut } from "@/components/charts/Donut";
import { QueryError } from "@/components/QueryError";

interface FinanceDashboard {
  kpis: {
    totalBilled: number;
    totalCollected: number;
    totalOverdue: number;
    collectionRate: number;
    ytdExpenses: number;
  };
  monthly: Array<{ period: string; billed: number; collected: number }>;
  expensesByCategory: Record<string, number>;
  budgetVsActual: Array<{ category: string; allocated: number; spent: number }>;
  fiscalYear: string | null;
}

export function FinanceDashboardPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("es") ? "es" : "en";
  const { data, isLoading, isError } = useQuery({
    queryKey: ["finance-dashboard"],
    queryFn: async () => (await api.get<FinanceDashboard>("/finance/dashboard")).data,
  });

  if (isError) return <QueryError />;
  if (isLoading || !data) return <div className="text-slate-500">{t("common.loading")}</div>;

  const kpis = [
    { label: t("finance.kpis.totalBilled"), value: formatCurrency(data.kpis.totalBilled), icon: <DollarSign className="text-slate-500" /> },
    { label: t("finance.kpis.totalCollected"), value: formatCurrency(data.kpis.totalCollected), icon: <TrendingUp className="text-green-500" /> },
    { label: t("finance.kpis.totalOverdue"), value: formatCurrency(data.kpis.totalOverdue), icon: <AlertTriangle className="text-red-500" /> },
    { label: t("finance.kpis.ytdExpenses"), value: formatCurrency(data.kpis.ytdExpenses), icon: <Receipt className="text-purple-500" /> },
  ];

  const categoryEntries = Object.entries(data.expensesByCategory);
  const maxCat = Math.max(1, ...categoryEntries.map(([, v]) => v));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">{t("finance.dashboard")}</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="card flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-500">{k.label}</div>
              <div className="text-2xl font-bold mt-1">{k.value}</div>
            </div>
            {k.icon}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card">
          <h2 className="text-lg font-semibold mb-3">{t("finance.kpis.collectionRate")}</h2>
          <Donut
            value={data.kpis.collectionRate}
            max={100}
            label={`${formatCurrency(data.kpis.totalCollected)} / ${formatCurrency(data.kpis.totalBilled)}`}
            sublabel={t("finance.kpis.collectionRate")}
            color="#0ea5e9"
          />
        </div>

        <div className="card lg:col-span-2">
          <h2 className="text-lg font-semibold mb-3">{t("finance.monthlyTrend")}</h2>
          <BarChart
            labels={data.monthly.map((m) => m.period.slice(5))}
            series={[
              { label: t("finance.billed"), color: "#cbd5e1", values: data.monthly.map((m) => m.billed) },
              { label: t("finance.collected"), color: "#0ea5e9", values: data.monthly.map((m) => m.collected) },
            ]}
            formatY={(v) => formatCurrency(v)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <h2 className="text-lg font-semibold mb-3">{t("finance.expensesByCategory")}</h2>
          {categoryEntries.length === 0 ? (
            <p className="text-sm text-slate-500">—</p>
          ) : (
            <ul className="space-y-2">
              {categoryEntries.map(([cat, val]) => (
                <li key={cat}>
                  <div className="flex justify-between text-sm mb-1">
                    <span>{expenseCategoryLabel(cat, lang)}</span>
                    <span className="font-medium">{formatCurrency(val)}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded overflow-hidden">
                    <div className="h-full bg-primary-500" style={{ width: `${(val / maxCat) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold mb-3">
            {t("finance.budgetVsActual")} {data.fiscalYear && <span className="text-sm text-slate-500">· {data.fiscalYear}</span>}
          </h2>
          {data.budgetVsActual.length === 0 ? (
            <p className="text-sm text-slate-500">—</p>
          ) : (
            <ul className="space-y-3">
              {data.budgetVsActual.map((b) => {
                const pct = b.allocated > 0 ? (b.spent / b.allocated) * 100 : 0;
                const over = pct > 100;
                return (
                  <li key={b.category}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{b.category}</span>
                      <span className={over ? "text-red-600 font-medium" : ""}>
                        {formatCurrency(b.spent)} / {formatCurrency(b.allocated)}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded overflow-hidden">
                      <div
                        className={`h-full ${over ? "bg-red-500" : "bg-green-500"}`}
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
